/**
 * Construit les métadonnées de colonnes (type, valeurs/min/max) à partir d'une
 * liste d'objets. Permet de typer les colonnes du DataGrid et de les filtrer.
 * @param {Object[]} items
 * @returns {Object} map { champ: { type, values? | min?, max? } }
 */
export function buildColsMeta(items) {
    const map = {};
    items.forEach(item =>
        Object.entries(item).forEach(([k, v]) => {
            if (!map[k]) map[k] = [];
            if (v !== null && v !== undefined && v !== '') map[k].push(v);
        })
    );
    return Object.fromEntries(
        Object.entries(map).map(([k, vals]) => {
            if (!vals.length) return [k, {type: 'string', values: []}];
            const nums = vals.map(Number);
            return nums.every(n => !isNaN(n))
                ? [k, {type: 'number', min: Math.min(...nums), max: Math.max(...nums)}]
                : [k, {type: 'string', values: [...new Set(vals.map(String))]}];
        })
    );
}

/**
 * Retourne le nombre d'étages estimé à partir de la hauteur et de l'année de construction.
 * @param {number} hauteurBat
 * @param {number} annee
 * @returns {number}
 */
export function getNbEtage(hauteurBat, annee) {
    let h;
    if (annee < 1920)      h = 3.3;
    else if (annee < 1948) h = 3;
    else if (annee < 1978) h = 2.8;
    else if (annee < 1983) h = 2.6;
    else                   h = 2.5;
    return Math.floor(hauteurBat / h);
}

/**
 * Calcule la surface utile d'un bâtiment à partir de sa hauteur moyenne,
 * de sa surface géométrique au sol et de son année de construction.
 * @param {number} hauteurMean
 * @param {number} sgeomGroupe
 * @param {number} annee
 * @returns {number|string}
 */
export function computeSurfUtile(hauteurMean, sgeomGroupe, annee) {
    if (!hauteurMean || !sgeomGroupe) return '';
    const nbEtage = getNbEtage(hauteurMean, annee);
    return Math.ceil(nbEtage * Number(sgeomGroupe) * 0.9);
}

/**
 * Calcule la surface utile totale d'un groupe de bâtiments en additionnant la
 * surface de chaque bâtiment distinct. La déduplication se fait sur
 * `batiment_construction_id` : un même bâtiment ne sera compté qu'une seule fois.
 * Les bâtiments sans identifiant sont toujours inclus (impossible de les dédupliquer).
 * @param {Object[]} batiments
 * @param {number}   annee_construction
 * @returns {number}
 */
export function computeSurface(batiments, annee_construction) {
    let total = 0;
    const seen = new Set();
    batiments.forEach(b => {
        const id = b.batiment_construction_id;
        if (id) {
            if (seen.has(id)) return;   // doublon → on ignore
            seen.add(id);
        }
        total += computeSurfUtile(b.hauteur, b.s_geom_cstr, annee_construction) || 0;
    });
    return total;
}

/**
 * Extrait longitude/latitude d'un champ `geom` GeoJSON (Point) et les ajoute
 * en propriétés de premier niveau sur chaque item.
 * @param {Object[]} items
 * @returns {Object[]}
 */
export function expandGeom(items) {
    return items.map(item => {
        if (!item.geom) return item;
        try {
            const g = typeof item.geom === 'string' ? JSON.parse(item.geom) : item.geom;
            if (g?.type === 'Point' && g.coordinates?.length >= 2)
                return {...item, longitude: g.coordinates[0], latitude: g.coordinates[1]};
        } catch { /* ignore */ }
        return item;
    });
}

